import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  type AdminCrmLead,
  adminApi,
  type CandidateBackfillResult,
  type RecruiterBackfillResult,
} from "@/api/admin.api";

const PAGE_SIZE = 50;

// Funnel values, as in apps/server/src/crm/crm.constants.ts (LEAD_STATUS).
// First Round's own statuses (theResume writes them) come after.
const STATUSES = [
  "Signed up",
  "Demo watched",
  "Email verified",
  "Demo booked",
  "Demo held",
  "Won",
  "Lost",
  "Demo feedback viewed",
];
// "local": saved before First Round had its own sync. "skipped": Zoho sync is off here.
const SYNC_STATUSES = ["pending", "synced", "failed", "skipped", "local"];
const BRANDS: { value: string; label: string }[] = [
  { value: "apex25", label: "Apex 25" },
  { value: "first-round", label: "First Round" },
];
const brandLabel = (brand: string) => BRANDS.find((item) => item.value === brand)?.label ?? brand;

// Zoho CRM (AU data centre): Apex 25 leads live in the Talentforge Innovations org (synced from
// here), First Round leads in the firstround.com.au org (synced by theResume).
const ZOHO_ORG: Record<string, string> = {
  apex25: "org7005330174",
  "first-round": "org7007671961",
};
const zohoLeadUrl = (brand: string, id: string) =>
  ZOHO_ORG[brand] ? `https://crm.zoho.com.au/crm/${ZOHO_ORG[brand]}/tab/Leads/${id}` : undefined;

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : "—");

const SYNC_TONE: Record<string, string> = {
  synced: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  pending: "bg-amber-100 text-amber-800",
  skipped: "bg-gray-100 text-gray-700",
  local: "bg-blue-50 text-blue-800",
};

const Recordings = ({ lead }: { lead: AdminCrmLead }) => {
  if (!lead.demoReplayUrl && !lead.resultsReplayUrl && !lead.recordingsUrl)
    return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-col gap-1">
      {lead.recordingsUrl && (
        <a
          className="text-blue-600 underline"
          href={lead.recordingsUrl}
          target="_blank"
          rel="noreferrer"
        >
          All recordings
        </a>
      )}
      {lead.demoReplayUrl && (
        <a
          className="text-blue-600 underline"
          href={lead.demoReplayUrl}
          target="_blank"
          rel="noreferrer"
        >
          Demo
        </a>
      )}
      {lead.resultsReplayUrl && (
        <a
          className="text-blue-600 underline"
          href={lead.resultsReplayUrl}
          target="_blank"
          rel="noreferrer"
        >
          Results
        </a>
      )}
    </div>
  );
};

type BackfillConsent = "inferred" | "none";
type BackfillResult = RecruiterBackfillResult | CandidateBackfillResult;

/** A one-off "import existing people as leads" panel: Preview (dry run) first, then Import. */
const BackfillPanel = ({
  id,
  title,
  description,
  noun,
  run: request,
  summary,
}: {
  id: string;
  title: string;
  description: string;
  noun: string;
  run: (body: { dryRun: boolean; consent: BackfillConsent }) => Promise<BackfillResult>;
  summary: (result: BackfillResult) => string;
}) => {
  const queryClient = useQueryClient();
  const [consent, setConsent] = useState<BackfillConsent>("none");
  const [preview, setPreview] = useState<BackfillResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const run = async (dryRun: boolean) => {
    setBusy(true);
    setFailed(false);
    try {
      setPreview(await request({ dryRun, consent }));
      if (!dryRun) await queryClient.invalidateQueries({ queryKey: ["admin-crm-leads"] });
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const choose = (value: BackfillConsent) => {
    setConsent(value);
    setPreview(null);
  };

  return (
    <details className="border rounded p-3 mb-4 text-sm">
      <summary className="cursor-pointer font-medium">{title}</summary>
      <p className="text-gray-600 mt-2">{description}</p>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name={`${id}-consent`}
            checked={consent === "none"}
            onChange={() => choose("none")}
          />
          No consent (non-marketing contacts)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name={`${id}-consent`}
            checked={consent === "inferred"}
            onChange={() => choose("inferred")}
          />
          Marketing consent inferred (existing customers)
        </label>
        <button
          type="button"
          className="border rounded px-3 py-1.5"
          disabled={busy}
          onClick={() => void run(true)}
        >
          Preview
        </button>
        {preview?.dryRun && preview.toImport > 0 && (
          <button
            type="button"
            className="rounded px-3 py-1.5 bg-amber-600 text-white"
            disabled={busy}
            onClick={() => void run(false)}
          >
            Import {preview.toImport} {noun}
            {preview.toImport === 1 ? "" : "s"}
          </button>
        )}
      </div>
      {failed && <p className="text-red-700 mt-2">That didn&apos;t work. Super admins only.</p>}
      {preview && (
        <p className="mt-2">
          {preview.dryRun ? "Preview" : "Done"}: {summary(preview)}{" "}
          {preview.dryRun
            ? `${preview.toImport} to import.`
            : `${preview.imported} imported and queued for Zoho.`}
        </p>
      )}
    </details>
  );
};

const BackfillRecruiters = () => (
  <BackfillPanel
    id="recruiters"
    title="Import existing recruiters (one-off)"
    description="Adds every recruiter who is not yet a lead as “Apex 25 sign-up / Signed up” in Apex 25's Zoho CRM. Existing leads and test addresses are left alone. Super admins only."
    noun="recruiter"
    run={adminApi.backfillRecruiters}
    summary={(result) => {
      const r = result as RecruiterBackfillResult;
      return `${r.recruiterRows} recruiter rows, ${r.distinctEmails} people, ${r.alreadyLeads} already leads, ${r.skippedTest} test and ${r.skippedNoEmail} without an email skipped.`;
    }}
  />
);

const BackfillCandidates = () => (
  <BackfillPanel
    id="candidates"
    title="Import existing candidates (theResume users, one-off)"
    description="Adds every verified theResume user who is not yet a First Round lead as “theResume user / Signed up”. theResume sends them to First Round's Zoho CRM, 50 every 10 minutes. Existing leads, unverified and test addresses are left alone. Super admins only."
    noun="candidate"
    run={adminApi.backfillCandidates}
    summary={(result) => {
      const r = result as CandidateBackfillResult;
      return `${r.candidates} candidates, ${r.alreadyLeads} already leads, ${r.skippedUnverified} unverified and ${r.skippedTest} test addresses skipped.`;
    }}
  />
);

/**
 * Read-only view of the CrmLead table: Apex 25 sign-ups, demo results and bookings, plus First
 * Round's leads (written by theResume). Our database is the source of truth; Zoho is synced from
 * it (production only; other environments show "skipped").
 */
export function CrmLeads() {
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [status, setStatus] = useState("");
  const [syncStatus, setSyncStatus] = useState("");
  const [page, setPage] = useState(0);

  const { data, isFetching, error } = useQuery({
    queryKey: ["admin-crm-leads", search, brand, status, syncStatus, page],
    queryFn: () =>
      adminApi.listCrmLeads({
        search: search || undefined,
        brand: brand || undefined,
        status: status || undefined,
        syncStatus: syncStatus || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const total = data?.total ?? 0;
  const pages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const resetPage =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(0);
    };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-1">CRM leads</h1>
      <p className="text-sm text-gray-600 mb-4">
        Leads from our database. Apex 25 leads (sign-ups, demos, bookings) sync to Apex 25&apos;s
        Zoho CRM in production (other environments show &quot;skipped&quot;); First Round leads sync
        to First Round&apos;s Zoho CRM from theResume.
      </p>

      <BackfillRecruiters />
      <BackfillCandidates />

      {data && (
        <div className="flex flex-wrap gap-2 mb-4 text-sm">
          {BRANDS.filter((item) => data.counts.brand?.[item.value]).map((item) => (
            <span key={item.value} className="border rounded px-2 py-1 bg-gray-50">
              {item.label}: <strong>{data.counts.brand[item.value]}</strong>
            </span>
          ))}
          {STATUSES.filter((value) => data.counts.status[value]).map((value) => (
            <span key={value} className="border rounded px-2 py-1 bg-white">
              {value}: <strong>{data.counts.status[value]}</strong>
            </span>
          ))}
          {SYNC_STATUSES.filter((value) => data.counts.syncStatus[value]).map((value) => (
            <span key={value} className={`rounded px-2 py-1 ${SYNC_TONE[value]}`}>
              Zoho {value}: <strong>{data.counts.syncStatus[value]}</strong>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          className="border p-2 rounded flex-1 min-w-[240px]"
          placeholder="Search by email, name, or company"
          value={search}
          onChange={(event) => resetPage(setSearch)(event.target.value)}
        />
        <select
          className="border p-2 rounded"
          value={brand}
          onChange={(event) => resetPage(setBrand)(event.target.value)}
          aria-label="Brand"
        >
          <option value="">All brands</option>
          {BRANDS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <select
          className="border p-2 rounded"
          value={status}
          onChange={(event) => resetPage(setStatus)(event.target.value)}
          aria-label="Lead status"
        >
          <option value="">All statuses</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select
          className="border p-2 rounded"
          value={syncStatus}
          onChange={(event) => resetPage(setSyncStatus)(event.target.value)}
          aria-label="Zoho sync"
        >
          <option value="">Any Zoho sync</option>
          {SYNC_STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p className="text-red-700 mb-4">
          Couldn&apos;t load leads. This page needs a business or super admin account.
        </p>
      )}

      <div className="border rounded overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Updated</th>
              <th className="p-3">Lead</th>
              <th className="p-3">Status</th>
              <th className="p-3">Consent</th>
              <th className="p-3">Attribution</th>
              <th className="p-3">Demo</th>
              <th className="p-3">Recordings</th>
              <th className="p-3">Zoho</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((lead) => (
              <tr key={lead.id} className="border-t align-top">
                <td className="p-3 whitespace-nowrap">{formatDate(lead.updatedAt)}</td>
                <td className="p-3">
                  <div className="font-medium">{lead.email}</div>
                  <div className="text-gray-600">
                    {[lead.name, lead.company].filter(Boolean).join(" · ") || "—"}
                  </div>
                </td>
                <td className="p-3">
                  <div>{lead.status}</div>
                  <div className="text-gray-500">
                    {brandLabel(lead.brand)} · {lead.source}
                  </div>
                </td>
                <td className="p-3">
                  {lead.marketingConsent ? (
                    <span title={formatDate(lead.marketingConsentAt)}>Yes</span>
                  ) : (
                    <span className="text-gray-500">No</span>
                  )}
                </td>
                <td className="p-3 text-gray-700">
                  {lead.utmSource || lead.utmMedium || lead.utmCampaign ? (
                    <div>
                      {[lead.utmSource, lead.utmMedium, lead.utmCampaign]
                        .map((value) => value ?? "-")
                        .join(" / ")}
                    </div>
                  ) : null}
                  <div className="text-gray-500 break-all">{lead.landingPage ?? "—"}</div>
                </td>
                <td className="p-3">
                  <div>{lead.demoRoleId ?? "—"}</div>
                  {lead.demoBookedAt && (
                    <div className="text-gray-600">Booked {formatDate(lead.demoBookedAt)}</div>
                  )}
                </td>
                <td className="p-3">
                  <Recordings lead={lead} />
                </td>
                <td className="p-3">
                  <span
                    className={`rounded px-2 py-0.5 ${SYNC_TONE[lead.syncStatus] ?? ""}`}
                    title={lead.syncError ?? undefined}
                  >
                    {lead.syncStatus}
                  </span>
                  {lead.zohoLeadId && (
                    <div className="mt-1">
                      <a
                        className="text-blue-600 underline"
                        href={zohoLeadUrl(lead.brand, lead.zohoLeadId)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in Zoho
                      </a>
                    </div>
                  )}
                  {lead.syncError && (
                    <div className="mt-1 text-red-700 max-w-[220px] break-words">
                      {lead.syncError}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 && (
              <tr>
                <td className="p-3 text-gray-500" colSpan={8}>
                  No leads match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-4 text-sm">
        <button
          className="underline disabled:opacity-40"
          disabled={page === 0 || isFetching}
          onClick={() => setPage((current) => current - 1)}
        >
          Previous
        </button>
        <span>
          Page {page + 1} of {pages} · {total} lead{total === 1 ? "" : "s"}
        </span>
        <button
          className="underline disabled:opacity-40"
          disabled={page + 1 >= pages || isFetching}
          onClick={() => setPage((current) => current + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
